/**
 * CEC-689 · las opciones del desplegable de impuestos de una línea de cotización.
 *
 * El comercial puede usar el impuesto de cualquier compañía (decisión de la PO del 23/09), pero el
 * catálogo trae cada impuesto repetido una vez por compañía: en PRE, doce «19% S». La PO decidió el
 * 01/10 ofrecer cada impuesto una sola vez, por nombre y porcentaje.
 *
 * Entre los repetidos gana, por este orden:
 *   1. el que la línea ya tiene — no se le cambia sin querer, ni sale dos veces en la lista;
 *   2. el de la compañía de la cotización;
 *   3. el de id más bajo.
 * Cada grupo sale en la posición en la que apareció por primera vez, así que la lista no se reordena.
 */
// Los campos van como `unknown` porque los catálogos de impuestos llegan sin tipar (drawers y rejilla).
export interface TaxOption {
  id: number;
  name?: unknown;
  amount?: unknown;
  taxType?: unknown;
  companyId?: unknown;
}

export function dedupeTaxOptions<T extends TaxOption>(
  taxes: readonly T[] | null | undefined,
  quoteCompanyId: number | null | undefined,
  currentTaxIds: unknown = [],
): T[] {
  const kept = new Set(toIds(currentTaxIds));
  const rank = (tax: T): number => {
    if (kept.has(Number(tax.id))) return 0;
    return quoteCompanyId != null && Number(tax.companyId) === Number(quoteCompanyId) ? 1 : 2;
  };

  const winners = new Map<string, T>();
  for (const tax of taxes || []) {
    const key = groupKey(tax);
    const current = winners.get(key);
    // A igual rango, el de id más bajo: el resultado no depende del orden en que llegue el catálogo.
    if (!current || rank(tax) < rank(current) || (rank(tax) === rank(current) && Number(tax.id) < Number(current.id))) {
      winners.set(key, tax);
    }
  }

  return [...winners.values()];
}

/**
 * CEC-689 · opción C, decisión de la PO del 05/10: el desplegable ofrece solo los impuestos de la compañía de
 * la cotización, más el que la línea ya tenga, que se conserva aunque sea de otra compañía (el servidor lo
 * sigue aceptando para poder editar las líneas antiguas). Sin compañía conocida no se filtra: mejor ofrecer
 * de más que dejar el desplegable vacío.
 */
export function restrictToQuoteCompany<T extends TaxOption>(
  taxes: readonly T[] | null | undefined,
  quoteCompanyId: number | null | undefined,
  currentTaxIds: unknown = [],
): T[] {
  if (quoteCompanyId == null) return [...(taxes || [])];
  const kept = new Set(toIds(currentTaxIds));
  return (taxes || []).filter((tax) => Number(tax.companyId) === Number(quoteCompanyId) || kept.has(Number(tax.id)));
}

function groupKey(tax: TaxOption): string {
  const name = String(tax.name ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  return `${tax.taxType ?? ''}|${name}|${taxPercentage(tax.amount)}`;
}

// El porcentaje llega como número o envuelto en `{ parsedValue }`, según de dónde venga el catálogo.
function taxPercentage(amount: unknown): number {
  const raw = (amount as { parsedValue?: unknown } | null)?.parsedValue ?? amount;
  return Number(raw) || 0;
}

function toIds(value: unknown): number[] {
  const list = Array.isArray(value) ? value : [value];
  return list.map((id) => Number(id)).filter((id) => id > 0);
}
